import {
  ConfigurationFormType,
  ConfigurationPageTables,
  ConfigurationPageType,
  ConfigurationTableFilterType,
} from "../interfaces";
import { FormatType } from "../../Alert/Filter/FilterType";
import ActivationImpactPanel from "../../PricingTier/ActivationImpactPanel";
import LadderForm from "../../PricingTier/LadderForm";

// An ordinary configuration table. Only the form is custom: a ladder is a
// variable-length matrix, which the declarative field types cannot express —
// the same escape hatch the group quoting studio uses.
const SETUP_PRICING_TIER_LADDERS_TAB: ConfigurationPageTables = {
  type: ConfigurationPageType.TABLE,
  label: "PRICING_TIER_LADDERS",
  path: "pricing-tier-ladders",
  description: "CONF_DESC_PRICING_TIER_LADDERS",
  model: {
    entityName: "name",
    label: "PRICING_TIER_LADDER",
    path: "/api/pricing-tier-grids",
    name: "pricing tier grid",
    attributes: [
      {
        className: "greyText",
        key: "name",
        order: 1,
        label: "NAME",
        visible: true,
      },
      {
        key: "property.hebergement_nom_court",
        order: 2,
        label: "PROPERTY",
        visible: true,
      },
      {
        align: "right",
        key: "version",
        order: 3,
        label: "PRICING_TIER_VERSION",
        visible: true,
      },
      {
        align: "right",
        key: "assignment_count",
        order: 4,
        label: "PRICING_TIER_USED_BY",
        visible: true,
      },
      {
        align: "right",
        key: "updated_at",
        order: 5,
        label: "UPDATED_AT",
        type: FormatType.DATE,
        visible: true,
      },
    ],
    filters: [
      { type: ConfigurationTableFilterType.Search },
      { type: ConfigurationTableFilterType.ResetButton },
    ],
  },
  options: {
    // The editor IS the form; the declarative builder has no field type for a
    // variable-length matrix.
    formFields: [],
    customForm: LadderForm,
    formSize: "lg",
    creatable: true,
    editable: true,
    copyable: false,
    deletable: true,
  },
};

// Assignments ARE the tier/relative switch: a scope listed here prices in
// tiers, anything absent keeps the relative (+x%) behaviour. That is why the
// tab description has to spell it out — nothing else in the app says it.
const SETUP_PRICING_TIER_ASSIGNMENTS_TAB: ConfigurationPageTables = {
  description: "CONF_DESC_PRICING_TIER_ASSIGNMENTS",
  type: ConfigurationPageType.TABLE,
  label: "PRICING_TIER_ASSIGNMENTS",
  path: "pricing-tier-assignments",
  model: {
    dynamicPath: true,
    entityName: "id",
    label: "PRICING_TIER_ASSIGNMENT",
    path: "/api/pricing-tier-assignments",
    name: "pricing tier assignment",
    attributes: [
      {
        key: "grid.name",
        order: 1,
        label: "PRICING_TIER_LADDER",
        visible: true,
      },
      {
        key: "property.hebergement_nom_court",
        order: 2,
        label: "PROPERTY",
        visible: true,
      },
      {
        key: "uh_type_nandc.short_name",
        order: 3,
        label: "UHTYPE_NC",
        visible: true,
      },
      {
        key: "uh_cat.short_name",
        order: 4,
        label: "UH_CAT",
        visible: true,
      },
      {
        key: "grid_type.label",
        order: 5,
        label: "GRID_TYPE",
        visible: true,
      },
      {
        align: "right",
        key: "date_from",
        order: 6,
        label: "OCCUPATION_START_DATE",
        type: FormatType.DATE,
        visible: true,
      },
      {
        align: "right",
        key: "date_to",
        order: 7,
        label: "OCCUPATION_END_DATE",
        type: FormatType.DATE,
        visible: true,
      },
      {
        key: "weekdays",
        order: 8,
        label: "DAYS_OF_OCCUPATION",
        type: FormatType.WEEKDAYS_ROUNDED,
        visible: true,
      },
      {
        key: "is_enabled",
        order: 9,
        label: "PRICING_TIER_ENABLED",
        type: FormatType.BOOLEAN,
        visible: true,
      },
    ],
    filters: [
      {
        type: ConfigurationTableFilterType.Select,
        api: "/api/properties/",
        apiParams: {
          page_size: 100,
          ordering: "hebergement_nom_court",
        },
        key: "property",
        labelKey: "hebergement_nom_court",
        label: "PROPERTY",
        lookup: "property",
      },
      {
        type: ConfigurationTableFilterType.Search,
      },
      {
        type: ConfigurationTableFilterType.ResetButton,
      },
    ],
  },
  options: {
    // The backend refuses two assignments of equal specificity that cover the
    // same cell; the form cannot express that rule, so its message must reach
    // the modal rather than a generic snackbar.
    displayFormError: true,
    formFields: [
      {
        key: "separatorLadder",
        label: "PRICING_TIER_SECTION_LADDER",
        subLabel: "PRICING_TIER_SECTION_LADDER_DESC",
        type: ConfigurationFormType.Separator,
      },
      {
        type: ConfigurationFormType.Select,
        api: "/api/pricing-tier-grids/",
        apiParams: {
          page_size: 200,
          ordering: "name",
        },
        key: "grid",
        label: "PRICING_TIER_LADDER",
        labelKey: "name",
        required: true,
      },
      {
        key: "separatorScope",
        label: "PRICING_TIER_SECTION_SCOPE",
        subLabel: "PRICING_TIER_SECTION_SCOPE_DESC",
        type: ConfigurationFormType.Separator,
      },
      // uh_type_nandc and uh_cat are mutually exclusive, same rule and same `hide`
      // mechanism as the price bounds form. Both narrow to the ladder’s own
      // property, which is why no property is asked for.
      // `uh-types-nandc`, not `uh-types`: the assignment stores a UhTypeNandc
      // id — that is what the resolver compares against a category's
      // `uh_type_nandc_id`. The other endpoint serves a different table whose
      // ids merely look alike.
      {
        type: ConfigurationFormType.Select,
        api: "/api/uh-types-nandc/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
          property: { path: "grid.property.id" },
        },
        key: "uh_type_nandc",
        label: "UHTYPE_NC",
        labelKey: "short_name",
        watchField: [
          {
            paramsKey: "property",
            paramsValue: "property.id",
            watchFieldPath: "grid",
          },
        ],
        relatedInputAction: { action: "hide", relatedInput: "uh_cat" },
      },
      {
        type: ConfigurationFormType.Select,
        api: "/api/uh-cats/",
        apiParams: {
          page_size: 100,
          ordering: "short_name",
          property: { path: "grid.property.id" },
        },
        key: "uh_cat",
        label: "UH_CAT",
        labelKey: "short_name",
        watchField: [
          {
            paramsKey: "property",
            paramsValue: "property.id",
            watchFieldPath: "grid",
          },
        ],
        relatedInputAction: { action: "hide", relatedInput: "uh_type_nandc" },
      },
      {
        type: ConfigurationFormType.Select,
        api: "/api/grid-types/",
        apiParams: {
          page_size: 100,
          ordering: "label",
        },
        key: "grid_type",
        label: "GRID_TYPE",
        labelKey: "label",
      },
      {
        key: "separatorPeriod",
        label: "PRICING_TIER_SECTION_PERIOD",
        subLabel: "PRICING_TIER_SECTION_PERIOD_DESC",
        type: ConfigurationFormType.Separator,
      },
      {
        key: "date_range_occ",
        startDateField: "date_from",
        endDateField: "date_to",
        startDateLabel: "OCCUPATION_START_DATE_INCLUDED",
        endDateLabel: "OCCUPATION_END_DATE_INCLUDED",
        type: ConfigurationFormType.DateRangePicker,
        defaultValue: null,
      },
      {
        key: "weekdays",
        label: "DAYS_OF_OCCUPATION",
        type: ConfigurationFormType.WeekDaysRounded,
      },
      {
        key: "is_enabled",
        label: "PRICING_TIER_ENABLED",
        type: ConfigurationFormType.Check,
        defaultValue: true,
      },
      // Activating an assignment is what actually moves prices, so the form
      // carries its own dry-run rather than asking the analyst to commit blind.
      {
        key: "activationImpact",
        label: "PRICING_TIER_SIMULATE_ACTIVATION",
        type: ConfigurationFormType.Warning,
        component: ActivationImpactPanel,
      },
    ],
    creatable: true,
    createOptions: [
      { key: "separatorLadder", flex: "100%" },
      { key: "grid", flex: "100%" },
      { key: "separatorScope", flex: "100%" },
      { key: "uh_type_nandc", flex: "50%" },
      { key: "uh_cat", flex: "50%" },
      { key: "grid_type", flex: "100%" },
      { key: "separatorPeriod", flex: "100%" },
      { key: "date_range_occ", flex: "100%" },
      { key: "weekdays", flex: "100%" },
      { key: "is_enabled", flex: "100%" },
      { key: "activationImpact", flex: "100%" },
    ],
    editable: true,
    editOptions: [
      { key: "separatorLadder", flex: "100%" },
      { key: "grid", flex: "100%" },
      { key: "separatorScope", flex: "100%" },
      { key: "uh_type_nandc", flex: "50%" },
      { key: "uh_cat", flex: "50%" },
      { key: "grid_type", flex: "100%" },
      { key: "separatorPeriod", flex: "100%" },
      { key: "date_range_occ", flex: "100%" },
      { key: "weekdays", flex: "100%" },
      { key: "is_enabled", flex: "100%" },
      { key: "activationImpact", flex: "100%" },
    ],
    copyable: false,
    deletable: true,
  },
};

export const SETUP_PRICING_TIER_PAGE = {
  type: ConfigurationPageType.TABS,
  count: 0,
  label: "PRICING_TIER",
  pageLabel: "PRICING_TIER_TITLE_SETUP",
  menuLabel: "PRICING_TIER",
  name: "PricingTier",
  path: "pricing-tier",
  // Opened to one customer while the feature settles. Honoured by both the
  // section menu and the router, so the page is neither listed nor reachable
  // by URL anywhere else.
  tabs: [SETUP_PRICING_TIER_LADDERS_TAB, SETUP_PRICING_TIER_ASSIGNMENTS_TAB],
};
